import React, { useState, useEffect } from 'react';
import {
  Modal,
  Title,
  Stack,
  Text,
  Button,
  Group,
  Divider,
  Tabs,
  Box,
  List,
  Alert,
  Select,
  LoadingOverlay,
  Textarea
} from '@mantine/core';
import {
  IconDownload,
  IconTable,
  IconFileAnalytics,
  IconChartBar,
  IconFilter,
  IconFileDescription
} from '@tabler/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { masterCategoryService } from 'services/lia/masterCategoryService';
import { assessmentScheduleService } from 'services/lia/assessmentScheduleService';
import { notifications } from '@mantine/notifications';

/* eslint-disable */
interface DownloadModalProps {
  opened: boolean;
  onClose: () => void;
  assessmentId: string;
  assessmentName: string;
  assessment?: any;
  openLiaReportModal: (selectedCategory?: string) => void;
  handleDownloadRawData: (selectedCategory?: string) => Promise<void>;
}
/* eslint-enable */

const DownloadModal: React.FC<DownloadModalProps> = ({
  opened,
  onClose,
  openLiaReportModal,
  handleDownloadRawData,
  assessment,
  assessmentId
}) => {
  const [selectedCategoryRaw, setSelectedCategoryRaw] = useState<string | null>(null);
  const [selectedCategoryLIA, setSelectedCategoryLIA] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [categorySearch, setCategorySearch] = useState('');
  const [summaryDescription, setSummaryDescription] = useState('');
  const [isSavingSummary, setIsSavingSummary] = useState(false);

  const queryClient = useQueryClient();
  // Use assessmentId from props (from URL)
  const assessmentScheduleId =
    assessmentId || assessment?.assessmentDetails?.msAssessmentScheduleID || '';

  // Extract unique categories from assessment questions
  const extractCategoriesFromAssessment = React.useMemo(() => {
    if (!assessment?.modules) return [];

    const categoriesSet = new Set<string>();

    assessment.modules.forEach((module: any) => {
      if (module.questions && Array.isArray(module.questions)) {
        module.questions.forEach((question: any) => {
          if (question.category) {
            // Handle comma-separated categories
            const categories = question.category.split(',').map((cat: string) => cat.trim());
            categories.forEach((cat: string) => {
              if (cat) categoriesSet.add(cat);
            });
          }
        });
      }
    });

    return Array.from(categoriesSet).sort();
  }, [assessment]);

  // Fetch categories from master category service
  const {
    data: categoriesResponse,
    isLoading: categoriesLoading,
    error: categoriesError
  } = useQuery({
    queryKey: ['masterCategories', categorySearch],
    queryFn: () =>
      masterCategoryService.getList({
        search: categorySearch,
        pageNumber: 1,
        pageSize: 100
      }),
    enabled: opened,
    refetchOnWindowFocus: false
  });

  // Fetch summary report data
  const { data: summaryReportData, isLoading: summaryReportLoading } = useQuery({
    queryKey: ['summaryReport', assessmentScheduleId],
    queryFn: () => assessmentScheduleService.getSummaryReport(assessmentScheduleId),
    enabled: opened && !!assessmentScheduleId,
    refetchOnWindowFocus: false
  });

  // Mutation for saving/updating summary report
  const summaryReportMutation = useMutation({
    mutationFn: (data: {
      trAssessmentReportSummary?: string;
      msAssessmentScheduleId: string;
      summaryDescription: string;
    }) => assessmentScheduleService.upsertSummaryReport(data),
    onSuccess: () => {
      notifications.show({
        title: 'Success',
        message: 'Summary report saved successfully',
        color: 'green'
      });
      queryClient.invalidateQueries({ queryKey: ['summaryReport', assessmentScheduleId] });
      setIsSavingSummary(false);
    },
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Failed to save summary report',
        color: 'red'
      });
      setIsSavingSummary(false);
    }
  });

  // eslint-disable-next-line no-unused-vars
  const categoryOptions =
    categoriesResponse?.data?.map((item) => ({
      value: item.category,
      label: item.category
    })) || [];

  // Combine categories from assessment questions and master category service
  const assessmentCategoryOptions = extractCategoriesFromAssessment.map((category) => ({
    value: category,
    label: category
  }));

  // Merge both sources and remove duplicates
  const allCategories = [...assessmentCategoryOptions];
  const uniqueCategories = Array.from(
    new Map(allCategories.map((item) => [item.value, item])).values()
  );

  const selectOptions = uniqueCategories;

  useEffect(() => {
    if (!opened) {
      setSelectedCategoryRaw(null);
      setSelectedCategoryLIA(null);
      setCategorySearch('');
      setIsDownloading(false);
      setSummaryDescription('');
      setIsSavingSummary(false);
    }
  }, [opened]);

  useEffect(() => {
    if (opened && summaryReportData) {
      // Extract first item from data array
      const summaryItem = summaryReportData?.data?.[0];
      setSummaryDescription(summaryItem?.summaryDescription || '');
    }
  }, [opened, summaryReportData]);

  /* eslint-disable */
  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const categoryToPass =
        selectedCategoryRaw === 'all' ? undefined : selectedCategoryRaw || undefined;
      await handleDownloadRawData(categoryToPass);
    } catch {
    } finally {
      setIsDownloading(false);
    }
  };
  /* eslint-enable */

  const handleOpenLIAReport = () => {
    const categoryToPass =
      selectedCategoryLIA === 'all' ? undefined : selectedCategoryLIA || undefined;
    openLiaReportModal(categoryToPass);
    onClose();
  };

  const handleSaveSummaryReport = async () => {
    if (!assessmentScheduleId) {
      notifications.show({
        title: 'Error',
        message: 'Assessment schedule ID not found',
        color: 'red'
      });
      return;
    }

    setIsSavingSummary(true);

    // Extract first item from data array
    const summaryItem = summaryReportData?.data?.[0];

    const payload = {
      msAssessmentScheduleId: assessmentScheduleId,
      summaryDescription: summaryDescription,
      ...(summaryItem?.trAssessmentReportSummary && {
        trAssessmentReportSummary: summaryItem.trAssessmentReportSummary
      })
    };

    summaryReportMutation.mutate(payload);
  };

  const renderCategoryFilter = (
    /* eslint-disable */
    selectedCategory: string | null,
    setSelectedCategory: (value: string | null) => void,
    label: string
    /* eslint-enable */
  ) => (
    <Box
      p='md'
      style={{
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        border: '1px solid #e9ecef'
      }}
      mb='lg'
    >
      <Group gap='xs' mb='sm'>
        <IconFilter size={16} />
        <Text fw={600} size='sm'>
          Filter Options
        </Text>
      </Group>

      <Select
        label={label}
        placeholder='Select a category to filter data'
        data={selectOptions}
        value={selectedCategory}
        onChange={setSelectedCategory}
        searchable
        onSearchChange={setCategorySearch}
        disabled={categoriesLoading || isDownloading}
        clearable
        description='Filter the exported data by category. Leave empty to export all data.'
        error={categoriesError ? 'Failed to load categories' : undefined}
      />

      {categoriesLoading && (
        <Text size='xs' c='dimmed' mt='xs'>
          Loading categories...
        </Text>
      )}
    </Box>
  );

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Title order={4}>Download Assessment Data</Title>}
      size='lg'
      p='lg'
      centered
      closeOnClickOutside={!isDownloading}
      closeOnEscape={!isDownloading}
      styles={{
        header: {
          backgroundColor: '#f8f9fa',
          padding: '16px 24px',
          borderTopLeftRadius: '8px',
          borderTopRightRadius: '8px'
        },
        body: { padding: '24px' }
      }}
    >
      <LoadingOverlay visible={isDownloading} overlayProps={{ blur: 2 }} />

      <Stack gap='md'>
        <Text size='sm'>
          Select the data you would like to export. You can download complete raw data or generate a
          Learning Impact Assessment report.
        </Text>

        <Tabs defaultValue='raw-data'>
          <Tabs.List>
            <Tabs.Tab value='raw-data' leftSection={<IconTable size={14} />}>
              Complete Raw Data
            </Tabs.Tab>
            <Tabs.Tab value='lia-report' leftSection={<IconFileAnalytics size={14} />}>
              LIA Report
            </Tabs.Tab>
            <Tabs.Tab value='summary-report' leftSection={<IconFileDescription size={14} />}>
              Summary Report
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value='raw-data' pt='md'>
            <Box p='md'>
              <Text mb='md'>
                This export includes all assessment data in a complete downloadable package:
              </Text>
              <List size='sm' mt='xs' mb='lg'>
                <List.Item>Assessment details, modules, and questions</List.Item>
                <List.Item>All participant and respondent information</List.Item>
                <List.Item>
                  All answers from participants, respondents, and external users
                </List.Item>
                <List.Item>All submitted evidence files with metadata</List.Item>
              </List>

              {renderCategoryFilter(
                selectedCategoryRaw,
                setSelectedCategoryRaw,
                'Category Filter for Raw Data'
              )}

              <Button
                leftSection={<IconDownload size={16} />}
                onClick={handleDownload}
                color='#7B2183'
                fullWidth
                loading={isDownloading}
                disabled={categoriesLoading}
              >
                {isDownloading ? 'Preparing Download...' : 'Download Complete Raw Data (XLSX)'}
              </Button>
            </Box>
          </Tabs.Panel>

          <Tabs.Panel value='lia-report' pt='md'>
            <Box p='md'>
              <Text>
                Generate a comprehensive Learning Impact Assessment (LIA) report with all the
                analysis data from this assessment.
              </Text>
              <List size='sm' mt='xs' mb='lg'>
                <List.Item>
                  Create a professional assessment report in the standard LIA format
                </List.Item>
                <List.Item>Edit the report content with a rich text editor</List.Item>
                <List.Item>Export the report to a Word document</List.Item>
              </List>

              {renderCategoryFilter(
                selectedCategoryLIA,
                setSelectedCategoryLIA,
                'Category Filter for LIA Report'
              )}

              <Alert
                icon={<IconChartBar size={16} />}
                title='LIA Report Editor'
                color='blue'
                variant='light'
                mb='md'
              >
                Use the LIA Report Editor to create a structured assessment report that follows the
                standard LIA format. The report includes participant and supervisor response
                analysis, business unit distribution, and both qualitative and quantitative
                findings.
              </Alert>

              <Button
                leftSection={<IconFileAnalytics size={16} />}
                onClick={handleOpenLIAReport}
                color='#7B2183'
                fullWidth
                disabled={isDownloading || categoriesLoading}
              >
                Open LIA Report Editor
              </Button>
            </Box>
          </Tabs.Panel>

          <Tabs.Panel value='summary-report' pt='md'>
            <Box p='md'>
              <Text mb='md'>
                Create or update the summary report for this assessment. This report summary will be
                used to provide an overview of the assessment results.
              </Text>

              {summaryReportLoading && (
                <Alert color='blue' mb='md'>
                  Loading summary report data...
                </Alert>
              )}

              {!summaryReportLoading && summaryReportData?.data?.[0]?.trAssessmentReportSummary && (
                <Alert color='green' mb='md' variant='light'>
                  Existing summary found. You can update it below.
                </Alert>
              )}

              {!summaryReportLoading &&
                !summaryReportData?.data?.[0]?.trAssessmentReportSummary && (
                  <Alert color='yellow' mb='md' variant='light'>
                    No existing summary report found. You can create a new one below.
                  </Alert>
                )}

              <Textarea
                label='Summary Report Description'
                placeholder='Enter the summary report description here...'
                value={summaryDescription}
                onChange={(event) => setSummaryDescription(event.currentTarget.value)}
                minRows={6}
                maxRows={12}
                mb='md'
                disabled={isSavingSummary || summaryReportLoading}
                description='Provide a comprehensive summary of the assessment including key findings, outcomes, and recommendations.'
              />

              <Button
                leftSection={<IconFileDescription size={16} />}
                onClick={handleSaveSummaryReport}
                color='#7B2183'
                fullWidth
                loading={isSavingSummary}
                disabled={
                  summaryReportLoading ||
                  !summaryDescription.trim() ||
                  !assessmentScheduleId ||
                  isSavingSummary
                }
              >
                {summaryReportData?.data?.[0]?.trAssessmentReportSummary
                  ? 'Update Summary Report'
                  : 'Create Summary Report'}
              </Button>
            </Box>
          </Tabs.Panel>
        </Tabs>

        <Divider my='md' />

        <Group justify='space-between'>
          <Button variant='outline' onClick={onClose} disabled={isDownloading}>
            Cancel
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default DownloadModal;
